import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Breadcrumbs from '../components/Breadcrumbs';
import { getAuthToken } from '../utils/authToken';

const API_BASE = '/api';

export default function AssignmentsPage() {
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const token = getAuthToken();

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchUserAndData();
  }, [token]);

  const fetchUserAndData = async () => {
    try {
      // Get user info
      const userRes = await axios.get(`${API_BASE}/me/`, {
        headers: { Authorization: `Token ${token}` }
      });
      setUser(userRes.data);

      // Check user role
      if (userRes.data.user_profile?.role !== 'teacher') {
        setError('Access denied. This page is for teachers only.');
        return;
      }

      // Fetch classes for filter
      const classRes = await axios.get(
        `${API_BASE}/school-classes/?teacher=${userRes.data.id}`,
        { headers: { Authorization: `Token ${token}` } }
      );
      setClasses(classRes.data.results || classRes.data || []);

      // Fetch all assignments
      await fetchAssignments();
    } catch (err) {
      if (err.response?.status === 401) {
        // Token is invalid, redirect to login
        localStorage.removeItem('token');
        navigate('/login');
        return;
      } else if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to view assignments.');
      } else {
        setError('Failed to load data');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async (classId = null) => {
    try {
      let url = `${API_BASE}/assignments/`;
      if (classId && classId !== 'all') {
        url += `?class_assigned=${classId}`;
      }

      const res = await axios.get(url, {
        headers: { Authorization: `Token ${token}` }
      });
      setAssignments(res.data.results || res.data || []);
    } catch (err) {
      setError('Failed to load assignments');
      console.error(err);
    }
  };

  const handleClassFilter = (classId) => {
    setSelectedClass(classId);
    fetchAssignments(classId);
  };

  const getSubmissionCount = (assignment) => {
    // This would need an API endpoint to get submission count
    // For now, showing placeholder
    return '—';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const breadcrumbPaths = [
    { name: 'Dashboard', path: '/' },
    { name: 'Assignments', path: '/assignments' }
  ];

  if (loading) {
    return (
      <div className="p-5">
        <Breadcrumbs paths={breadcrumbPaths} />
        <p>Loading assignments...</p>
      </div>
    );
  }

  return (
    <div className="p-5">
      <Breadcrumbs paths={breadcrumbPaths} />
      
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">All Assignments</h1>
        
        {/* Class Filter */}
        <div className="mb-4">
          <label className="mr-2 font-medium">Filter by class:</label>
          <select 
            value={selectedClass} 
            onChange={(e) => handleClassFilter(e.target.value)}
            className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Classes</option>
            {classes.map(cls => (
              <option key={cls.id} value={cls.id}>
                {cls.name} ({cls.subject})
              </option>
            ))}
          </select>
        </div>

        {/* Create New Assignment Button */}
        <Link
          to="/assignments/new"
          className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Create New Assignment
        </Link>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Assignments Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 border text-left">Title</th>
              <th className="px-4 py-2 border text-left">Class</th>
              <th className="px-4 py-2 border text-left">Subject</th>
              <th className="px-4 py-2 border text-left">Due Date</th>
              <th className="px-4 py-2 border text-center">Max Points</th>
              <th className="px-4 py-2 border text-center">Submissions</th>
              <th className="px-4 py-2 border text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  No assignments found. Create your first assignment to get started.
                </td>
              </tr>
            ) : (
              assignments.map(assignment => (
                <tr key={assignment.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 border">
                    <Link 
                      to={`/assignments/${assignment.id}`}
                      className="text-blue-600 hover:underline font-medium"
                    >
                      {assignment.title}
                    </Link>
                  </td>
                  <td className="px-4 py-2 border">
                    {assignment.class_details?.name || '—'}
                  </td>
                  <td className="px-4 py-2 border">
                    {assignment.class_details?.subject || '—'}
                  </td>
                  <td className="px-4 py-2 border">
                    {formatDate(assignment.due_date)}
                  </td>
                  <td className="px-4 py-2 border text-center">
                    {assignment.max_points || '100'}
                  </td>
                  <td className="px-4 py-2 border text-center">
                    {getSubmissionCount(assignment)}
                  </td>
                  <td className="px-4 py-2 border">
                    <div className="flex justify-center space-x-2">
                      <Link
                        to="/fast-mark"
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        Fast Mark
                      </Link>
                      <Link
                        to={`/assignments/${assignment.id}`}
                        className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                      >
                        View/Edit
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}